/* California-only MSRP disclosures. Promoted from Elevate variation B, 2026-09-07.
 * Real IP lookup; QA_LOCATION must remain null in production.
 */
(async () => {
  'use strict';

  const GEO_ENDPOINT = 'https://smooche-geo.hello4768.workers.dev/';
  const QA_LOCATION = null; // Preview only: 'US-CA', 'US-NY', 'CA-ON', or 'unknown'.
  const TOOLTIP_TEXT = "The strikethrough price is the Manufacturer's Suggested Retail Price (MSRP).";
  const CACHE_KEY = 'smooche-compare-geo-v1:' + GEO_ENDPOINT;
  const CACHE_MS = 5 * 60 * 1000;
  const UI = 'data-smooche-msrp-ui';
  const KACHING_SELECTOR = '.kaching-bundles__bar-full-price,.kaching-bundles__free-gift__full-price';
  const KACHING_MARKER = 'data-smooche-msrp-kaching-price';
  const COLLECTION_SELECTOR = '.product-grid-item .product-grid-item__price #salewrap > s';
  const COLLECTION_MARKER = 'data-smooche-msrp-collection-price';
  const SELECTOR = [
    '[data-ca-compare-at]',
    '.product__price--compare',
    's[data-compare-price]', // Some other [data-compare-price] nodes are purchasable options.
    '[data-compare-price-wrapper]',
    '.product-grid-item #salewrap > s',
    '.sb-buybox__compare-price',
    '.sb-buybox__bundle-compare',
    '.ssb__tier-compare',
    '.ssb__atc-compare',
    's[data-ls-bundle-compare]',
    '.kaching-popup-product-price__strikethrough',
    KACHING_SELECTOR
  ].join(',');

  // Also undoes the previous hide-prices treatment if replaced in a preview.
  window.SmoocheCompareAtExperiment?.stop();
  let stopped = false;
  let observer;
  let resizeObserver;
  let popupTimer;
  let frame;
  let closeTimer;
  let style;
  let tooltip;
  let activeButton = null;
  let pinned = false;
  const controller = new AbortController();
  const pairs = new Map();
  const popupRoots = new Map();
  const state = { status: 'loading', location: null, applied: false, buttons: 0, qa: Boolean(QA_LOCATION) };

  function report(status, location = null) {
    Object.assign(state, { status, location, applied: status === 'annotated' });
    // Diagnostic only; this event does not set an Elevate audience or analytics segment.
    window.dispatchEvent(new CustomEvent('smooche:compare-at', { detail: { ...state } }));
  }

  function listen(target, event, handler, options = {}) {
    target.addEventListener(event, handler, { ...options, signal: controller.signal });
  }

  function close() {
    clearTimeout(closeTimer);
    if (activeButton) activeButton.setAttribute('aria-expanded', 'false');
    activeButton = null;
    pinned = false;
    if (tooltip) tooltip.hidden = true;
  }

  function stop() {
    stopped = true;
    controller.abort();
    observer?.disconnect();
    resizeObserver?.disconnect();
    clearInterval(popupTimer);
    for (const entry of popupRoots.values()) entry.style.remove();
    popupRoots.clear();
    cancelAnimationFrame(frame);
    close();
    for (const [price, button] of pairs) {
      price.removeAttribute(KACHING_MARKER);
      price.removeAttribute(COLLECTION_MARKER);
      button.remove();
    }
    pairs.clear();
    tooltip?.remove();
    style?.remove();
    state.buttons = 0;
    report('stopped');
  }
  window.SmoocheCompareAtExperiment = { state, stop };

  function validate(value) {
    return value && !value.error && value.country_code !== 'XX' && /^[A-Z]{2}$/.test(value.country_code) &&
      (value.country_code !== 'US' || /^[A-Z]{2}$/.test(value.region_code));
  }

  async function locate() {
    if (QA_LOCATION) {
      if (QA_LOCATION === 'unknown') throw new Error('Simulated unknown location');
      const [country_code, region_code] = QA_LOCATION.split('-');
      const result = { country_code, region_code };
      if (!validate(result)) throw new Error('Invalid QA location');
      return result;
    }
    if (!GEO_ENDPOINT) throw new Error('Set GEO_ENDPOINT before running this experiment');
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (cached && cached.expires > Date.now() &&
          cached.expires <= Date.now() + CACHE_MS && validate(cached.location)) {
        return cached.location;
      }
    } catch (_) { /* Storage may be blocked; the lookup can still run. */ }

    const timeout = setTimeout(() => controller.abort(), 2000);
    try {
      const response = await fetch(GEO_ENDPOINT, {
        signal: controller.signal, credentials: 'omit', cache: 'no-store',
        referrerPolicy: 'no-referrer'
      });
      if (!response.ok) throw new Error('Location lookup failed');
      const value = await response.json();
      if (!validate(value)) throw new Error('Location is unknown');
      // Retain only country/state, never a visitor's IP or precise location.
      const location = { country_code: value.country_code, region_code: value.region_code || null };
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ location, expires: Date.now() + CACHE_MS }));
      } catch (_) { /* Nonessential cache. */ }
      return location;
    } finally {
      clearTimeout(timeout);
    }
  }

  function positionTooltip() {
    if (!activeButton || !tooltip || tooltip.hidden) return;
    if (!activeButton.isConnected || activeButton.hidden || !activeButton.getClientRects().length) {
      close();
      return;
    }
    const rect = activeButton.getBoundingClientRect();
    const viewport = window.visualViewport;
    const leftEdge = viewport?.offsetLeft || 0;
    const topEdge = viewport?.offsetTop || 0;
    const width = viewport?.width || document.documentElement.clientWidth || window.innerWidth;
    const height = viewport?.height || window.innerHeight;
    if (rect.bottom < topEdge || rect.top > topEdge + height) { close(); return; }
    tooltip.style.maxWidth = Math.max(120, width - 24) + 'px';
    const box = tooltip.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const left = Math.max(leftEdge + 12, Math.min(center - box.width / 2, leftEdge + width - box.width - 12));
    const below = rect.bottom + box.height + 8 <= topEdge + height - 12;
    const top = below ? rect.bottom + 8 : Math.max(topEdge + 12, rect.top - box.height - 8);
    tooltip.style.left = left + 'px';
    tooltip.style.top = top + 'px';
    tooltip.style.setProperty('--msrp-arrow-left', Math.max(12, Math.min(box.width - 12, center - left)) + 'px');
    tooltip.dataset.side = below ? 'below' : 'above';
  }

  function open(button) {
    if (stopped || button.hidden) return;
    clearTimeout(closeTimer);
    if (activeButton && activeButton !== button) close();
    activeButton = button;
    // Kaching's upsell lives in an open shadow root at the maximum z-index.
    // Keep its tooltip in that same root, outside the clipped modal container.
    const root = button.getRootNode();
    const container = root === document ? document.body : root;
    if (tooltip.parentNode !== container) container.appendChild(tooltip);
    button.setAttribute('aria-expanded', 'true');
    tooltip.hidden = false;
    positionTooltip();
  }

  function scheduleClose() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      if (!pinned && activeButton?.getRootNode().activeElement !== activeButton) close();
    }, 180);
  }

  function makeButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'smooche-msrp-info';
    button.setAttribute(UI, 'button');
    button.setAttribute('aria-label', 'About the MSRP price');
    button.setAttribute('aria-describedby', tooltip.id);
    button.setAttribute('aria-controls', tooltip.id);
    button.setAttribute('aria-expanded', 'false');
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = 'i';
    button.appendChild(icon);
    listen(button, 'pointerenter', event => { if (event.pointerType !== 'touch') open(button); });
    listen(button, 'pointerleave', scheduleClose);
    listen(button, 'focus', () => open(button));
    listen(button, 'blur', () => { if (activeButton === button) close(); });
    listen(button, 'click', event => {
      event.preventDefault();
      event.stopPropagation();
      if (activeButton === button && pinned) close();
      else { open(button); pinned = true; }
    });
    return button;
  }

  function placement(price) {
    // Place outside links/labels/buttons instead of nesting another interactive control.
    // For linked collection prices the info button follows the complete price link.
    let anchor = price;
    for (let node = price.parentElement; node && node !== document.body; node = node.parentElement) {
      if (node.matches('a,button,label,[role="button"],[role="link"]')) anchor = node;
    }
    return anchor;
  }

  function positionButton(price, button) {
    if (button.dataset.msrpLayout === 'kaching' || button.dataset.msrpLayout === 'collection') {
      // Keep the button outside the price link / bundle label, but visually
      // align it with the reference price instead of adding a separate flex row.
      const parent = button.offsetParent;
      if (!parent) return;
      const rect = price.getBoundingClientRect();
      const box = parent.getBoundingClientRect();
      const scaleX = parent.offsetWidth ? box.width / parent.offsetWidth : 1;
      const scaleY = parent.offsetHeight ? box.height / parent.offsetHeight : 1;
      const x = (rect.right - box.left) / (scaleX || 1) - parent.clientLeft + parent.scrollLeft - 18;
      const y = (rect.top + rect.height / 2 - box.top) / (scaleY || 1) - parent.clientTop + parent.scrollTop - 12;
      button.style.left = x + 'px';
      button.style.top = y + 'px';
      // Gift values are white on the selected pink gift rows.
      button.style.color = price.matches('.kaching-bundles__free-gift__full-price') ? getComputedStyle(price).color : '';
    } else {
      // The theme uses a 9px flex gap. Cancel it only around this button,
      // producing 2px before the icon slot and 6px after instead of two full gaps.
      const css = getComputedStyle(button.parentElement);
      const gap = css.display.includes('flex') && css.flexDirection.startsWith('row') ? parseFloat(css.columnGap) || 0 : 0;
      button.style.setProperty('--msrp-parent-gap', gap + 'px');
    }
  }

  function sync() {
    if (stopped) return;
    discoverPopupRoots();
    for (const [price, button] of pairs) {
      if (!price.isConnected || !price.matches(SELECTOR)) {
        if (activeButton === button) close();
        button.remove();
        price.removeAttribute(KACHING_MARKER);
        price.removeAttribute(COLLECTION_MARKER);
        pairs.delete(price);
      }
    }
    for (const root of [document, ...popupRoots.keys()]) {
      for (const price of root.querySelectorAll(SELECTOR)) {
        const anchor = placement(price);
        if (!anchor.parentElement) continue;
        // Empty, zero, or hidden reference prices should never have a floating info icon.
        const text = price.textContent.trim();
        const css = getComputedStyle(price);
        const visible = /[1-9]/.test(text) && price.getClientRects().length > 0 &&
          css.display !== 'none' && css.visibility !== 'hidden' && css.visibility !== 'collapse' &&
          !price.closest('[hidden],[inert]');
        let button = pairs.get(price);
        if (!button && !visible) continue;
        const kaching = price.matches(KACHING_SELECTOR);
        const collection = price.matches(COLLECTION_SELECTOR);
        // Padding reserves an icon slot without putting text/buttons inside the
        // price node that Kaching and the theme read for calculations and syncing.
        if (kaching && visible) {
          if (!price.hasAttribute(KACHING_MARKER)) price.setAttribute(KACHING_MARKER, '');
        } else if (price.hasAttribute(KACHING_MARKER)) price.removeAttribute(KACHING_MARKER);
        if (collection && visible) {
          if (!price.hasAttribute(COLLECTION_MARKER)) price.setAttribute(COLLECTION_MARKER, '');
        } else if (price.hasAttribute(COLLECTION_MARKER)) price.removeAttribute(COLLECTION_MARKER);
        if (!button || !button.isConnected) {
          if (button === activeButton) close();
          button = makeButton();
          pairs.set(price, button);
        }
        button.dataset.msrpLayout = kaching ? 'kaching' : collection ? 'collection' : 'inline';
        // Account for multiple reference prices inside the same linked card/label.
        const anchorButtons = [...pairs.entries()].filter(([other]) => placement(other) === anchor).map(([, b]) => b);
        const index = anchorButtons.indexOf(button);
        const previous = index > 0 ? anchorButtons[index - 1] : anchor;
        if (previous.isConnected && previous.nextElementSibling !== button) previous.after(button);
        if (button.hidden === visible) button.hidden = !visible;
        const label = 'About the MSRP price of ' + text;
        if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
        if (!visible && activeButton === button) close();
      }
    }
    // Measure after all padding/insertion writes, so every Kaching row uses its
    // final dimensions rather than the pre-injection price-column width.
    for (const [price, button] of pairs) if (!button.hidden) positionButton(price, button);
    state.buttons = [...pairs.values()].filter(button => !button.hidden).length;
    positionTooltip();
  }

  function scheduleSync() {
    if (stopped || frame) return;
    frame = requestAnimationFrame(() => { frame = null; sync(); });
  }

  function ownNode(node) {
    const element = node.nodeType === 1 ? node : node.parentElement;
    return !!element?.closest('[' + UI + ']');
  }

  const observerOptions = {
    childList: true, characterData: true, subtree: true, attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'inert', 'aria-hidden']
  };

  function discoverPopupRoots() {
    let changed = false;
    for (const [root, entry] of popupRoots) {
      if (!root.host.isConnected) {
        entry.style.remove();
        popupRoots.delete(root);
        changed = true;
      }
    }
    for (const host of document.querySelectorAll('kaching-popup-v2')) {
      const root = host.shadowRoot;
      if (!root || popupRoots.has(root)) continue;
      const shadowStyle = style.cloneNode(true);
      root.appendChild(shadowStyle);
      popupRoots.set(root, { style: shadowStyle });
      observer.observe(root, observerOptions);
      listen(root, 'scroll', positionTooltip, { capture: true, passive: true });
      listen(root, 'load', scheduleSync, { capture: true });
      changed = true;
    }
    return changed;
  }

  try {
    const location = await locate();
    if (stopped) return;
    if (location.country_code !== 'US' || location.region_code !== 'CA') {
      report('not-california', location);
      return;
    }
    style = document.createElement('style');
    style.id = 'smooche-ca-compare-style';
    style.setAttribute(UI, 'style');
    style.textContent = `
      button.smooche-msrp-info {
        all: unset; box-sizing: border-box; display: inline-flex !important;
        position: relative; align-items: center; justify-content: center; flex: 0 0 16px;
        width: 16px; height: 24px; min-width: 16px; min-height: 24px;
        margin: 0; margin-inline-start: calc(2px - var(--msrp-parent-gap, 0px));
        margin-inline-end: calc(6px - var(--msrp-parent-gap, 0px)); padding: 0; vertical-align: middle;
        color: #757575; cursor: help; text-decoration: none;
        border-radius: 50%; touch-action: manipulation;
      }
      button.smooche-msrp-info::before { content: ''; position: absolute; inset: 0 -4px; }
      button.smooche-msrp-info[data-msrp-layout="kaching"],
      button.smooche-msrp-info[data-msrp-layout="collection"] { position: absolute; margin: 0 !important; z-index: 2; }
      [data-smooche-msrp-kaching-price] { padding-right: 20px !important; }
      [data-smooche-msrp-collection-price] { padding-right: 20px !important; white-space: nowrap; }
      button.smooche-msrp-info > span {
        box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center;
        width: 14px; height: 14px; border: 1px solid currentColor; border-radius: 50%;
        font: 600 10px/1 Arial, sans-serif; text-decoration: none;
      }
      button.smooche-msrp-info:hover, button.smooche-msrp-info[aria-expanded="true"] { color: #222; }
      button.smooche-msrp-info:focus-visible { outline: 2px solid #333; outline-offset: 2px; }
      button.smooche-msrp-info[hidden], #smooche-msrp-tooltip[hidden] { display: none !important; }
      #smooche-msrp-tooltip {
        box-sizing: border-box; position: fixed; z-index: 2147483647; width: 246px;
        margin: 0; padding: 12px 14px; border: 1px solid #dedede; border-radius: 5px;
        background: #fff; color: #333; box-shadow: 0 3px 12px rgb(0 0 0 / 12%);
        font: 400 13px/1.45 Arial, sans-serif; letter-spacing: normal; text-transform: none;
        text-align: left; text-decoration: none; overflow-wrap: break-word;
      }
      #smooche-msrp-tooltip::before {
        content: ''; position: absolute; width: 8px; height: 8px; background: #fff;
        left: var(--msrp-arrow-left, 20px); transform: translateX(-50%) rotate(45deg);
      }
      #smooche-msrp-tooltip[data-side="below"]::before { top: -5px; border-top: 1px solid #dedede; border-left: 1px solid #dedede; }
      #smooche-msrp-tooltip[data-side="above"]::before { bottom: -5px; border-bottom: 1px solid #dedede; border-right: 1px solid #dedede; }
    `;
    tooltip = document.createElement('div');
    tooltip.id = 'smooche-msrp-tooltip';
    tooltip.setAttribute(UI, 'tooltip');
    tooltip.setAttribute('role', 'tooltip');
    tooltip.textContent = TOOLTIP_TEXT;
    tooltip.hidden = true;
    (document.head || document.documentElement).appendChild(style);
    (document.body || document.documentElement).appendChild(tooltip);
    listen(tooltip, 'pointerenter', () => clearTimeout(closeTimer));
    listen(tooltip, 'pointerleave', scheduleClose);
    listen(document, 'pointerdown', event => {
      const path = event.composedPath();
      if (activeButton && !path.includes(activeButton) && !path.includes(tooltip)) close();
    }, { capture: true });
    listen(document, 'keydown', event => {
      if (event.key === 'Escape' && activeButton) {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }, { capture: true });
    listen(window, 'resize', scheduleSync);
    listen(document, 'load', scheduleSync, { capture: true });
    listen(window, 'scroll', positionTooltip, { capture: true, passive: true });
    if (window.visualViewport) {
      listen(window.visualViewport, 'resize', scheduleSync);
      listen(window.visualViewport, 'scroll', positionTooltip);
    }
    observer = new MutationObserver(records => {
      if (records.some(record => !ownNode(record.target) && (record.type !== 'childList' ||
          [...record.addedNodes, ...record.removedNodes].some(node => !ownNode(node))))) scheduleSync();
    });
    observer.observe(document.documentElement, observerOptions);
    sync();
    // Attaching a shadow root to an existing host does not emit a document mutation.
    popupTimer = setInterval(() => { if (discoverPopupRoots()) scheduleSync(); }, 500);
    if (typeof ResizeObserver !== 'undefined' && document.body) {
      resizeObserver = new ResizeObserver(scheduleSync);
      resizeObserver.observe(document.body);
    }
    report('annotated', location);
  } catch (error) {
    if (stopped) return;
    stop();
    report('unknown');
    console.warn('[Smooche MSRP tooltip]', error.message);
  }
})();
